import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Col, Form, Input, InputNumber, Radio, Row, Spin, Tag, Typography, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import { isEqual } from 'lodash';
import { formatDate, sponsorTierTitle } from '../../../utilities';
import { adminCard, adminColors, SITE_FOOTER_CLEARANCE } from './adminStyles';

const { Text, Title } = Typography;

const useStyles = makeStyles((theme) => ({
  form: {
    maxWidth: 880,
    margin: '0 auto',
  },
  section: {
    ...adminCard,
    marginBottom: 16,
    padding: '16px 20px 4px',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 12,
      padding: '12px 12px 0',
    },
  },
  sectionHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  sectionTitle: {
    marginRight: 'auto',
  },
  tier: {
    marginBottom: 16,
    padding: '12px 12px 4px',
    backgroundColor: adminColors.canvas,
    border: `1px solid ${adminColors.border}`,
    borderRadius: 8,
  },
  tierHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 12,
  },
  tierTitle: {
    flex: 1,
    minWidth: 0,
  },
  subList: {
    marginBottom: 16,
  },
  listLabel: {
    display: 'block',
    marginBottom: 8,
  },
  listRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    '& .ant-form-item': {
      flex: 1,
      minWidth: 0,
      marginBottom: 8,
    },
  },
  sponsorRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) auto',
    gap: 8,
    '& .ant-form-item': {
      marginBottom: 8,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      '& .ant-form-item:not(:first-child)': {
        gridColumn: 1,
      },
      '& > .ant-btn': {
        gridColumn: 2,
        gridRow: 1,
      },
    },
  },
  saveBar: {
    position: 'sticky',
    bottom: SITE_FOOTER_CLEARANCE,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    maxWidth: 880,
    margin: '16px auto 0',
    padding: '10px 16px',
    backgroundColor: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: 8,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 48,
  },
}));

const REQUIRED = { required: true, message: 'Required' };

const STATUS_TAGS = {
  open: { color: 'green', label: 'Open' },
  upcoming: { color: 'blue', label: 'Not open yet' },
  closed: { color: 'default', label: 'Closed' },
  full: { color: 'orange', label: 'Full' },
};

const newTier = () => ({
  name: '',
  price: null,
  description: '',
  benefits: [],
  spots: 1,
  showOnHomePage: false,
  sponsors: [],
});

// Google's "Embed a map" gives a whole <iframe>, so keep just its link when that gets pasted.
const embedLink = (value = '') => {
  const match = value.match(/src="([^"]+)"/);
  return match ? match[1] : value;
};

// The API sends settings along with where registration stands. Split them apart.
const splitResponse = ({ registrationStatus, totalPlayers, ...settings }) => ({
  settings,
  registrationState: { registrationStatus, totalPlayers },
});

export default function TournamentSettingsForm({ onDirtyChange }) {
  const styles = useStyles();
  const [form] = Form.useForm();
  const [saved, setSaved] = useState(null);
  const [registrationState, setRegistrationState] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    fetch('/api/settings', { credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load tournament settings');
        return response.json();
      })
      .then((body) => {
        if (!active) return;
        const { settings, registrationState: state } = splitResponse(body);
        setSaved(settings);
        setRegistrationState(state);
      })
      .catch(() => active && setLoadFailed(true));

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;

    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [dirty]);

  const refreshDirty = () => {
    setDirty(!isEqual(form.getFieldsValue(true), saved));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form.getFieldsValue(true)),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body.message || 'Failed to save tournament settings.');
      }

      // The server trims text and drops blank benefits, so show what it kept.
      const { settings, registrationState: state } = splitResponse(body);
      form.setFieldsValue(settings);
      setSaved(settings);
      setRegistrationState(state);
      setDirty(false);
      message.success('Tournament settings saved');
    } catch (error) {
      message.error(error.message);
      console.error('Error saving tournament settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    form.resetFields();
    setDirty(false);
  };

  if (loadFailed) {
    return <Alert type="error" showIcon message="Couldn't load the tournament settings. Reload the page to try again." />;
  }

  // React 17 renders between the two state updates after a fetch, so wait for both.
  if (!saved || !registrationState) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  const status = STATUS_TAGS[registrationState.registrationStatus];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        className={styles.form}
        initialValues={saved}
        onValuesChange={refreshDirty}
        onFinish={handleSave}
        onFinishFailed={() => message.error('Some fields need attention before saving.')}
        scrollToFirstError
      >
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={5} className={styles.sectionTitle} style={{ margin: 0 }}>Event details</Title>
          </div>
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item label="Date" name={['event', 'date']} rules={[REQUIRED]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Check-in starts" name={['event', 'checkInStart']} rules={[REQUIRED]}>
                <Input type="time" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Check-in ends" name={['event', 'checkInEnd']} rules={[REQUIRED]}>
                <Input type="time" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Tee off" name={['event', 'teeOff']} rules={[REQUIRED]}>
                <Input type="time" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Format" name={['event', 'format']} rules={[REQUIRED]} extra="Like “4-person scramble”.">
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Course" name={['event', 'courseName']} rules={[REQUIRED]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="City" name={['event', 'courseCity']} rules={[REQUIRED]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Map link"
            name={['event', 'mapEmbedUrl']}
            rules={[REQUIRED]}
            normalize={embedLink}
            extra="In Google Maps, choose Share, then Embed a map, and paste what it gives you."
          >
            <Input />
          </Form.Item>
          <Form.Item label="After play" name={['event', 'afterPlayText']} extra="Optional. Shown on the event details page.">
            <Input />
          </Form.Item>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={5} className={styles.sectionTitle} style={{ margin: 0 }}>Registration</Title>
            <Tag color={status.color} style={{ margin: 0 }}>
              {registrationState.registrationStatus === 'upcoming'
                ? `Opens ${formatDate(saved.registration.openDate)}`
                : status.label}
            </Tag>
            <Text type="secondary">
              {registrationState.totalPlayers} of {saved.registration.playerCap} players
            </Text>
          </div>
          <Form.Item label="Status" name={['registration', 'override']}>
            <Radio.Group
              optionType="button"
              options={[
                { label: 'Follow the dates', value: 'auto' },
                { label: 'Force open', value: 'open' },
                { label: 'Force closed', value: 'closed' },
              ]}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label="Opens" name={['registration', 'openDate']} rules={[REQUIRED]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Closes"
                name={['registration', 'closeDate']}
                extra="Optional. Registration stays open through this day. Leave blank to stay open until it's full."
              >
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Price per player ($)" name={['registration', 'pricePerPlayer']} rules={[REQUIRED]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                label="Player cap"
                name={['registration', 'playerCap']}
                rules={[REQUIRED]}
                extra="Up to 144, the most the hole board can seat."
              >
                <InputNumber min={4} max={144} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                label="Show “spots left” at"
                name={['registration', 'spotsLeftShownAt']}
                rules={[REQUIRED]}
                extra="Players registered."
              >
                <InputNumber min={0} max={144} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Before registration opens"
            name={['registration', 'upcomingMessage']}
            rules={[REQUIRED]}
            extra="Shown under “Player registration will open on …!”"
          >
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
          <Form.Item
            label="After registration closes"
            name={['registration', 'closedMessage']}
            rules={[REQUIRED]}
            extra="Shown under “Player registration is closed.”"
          >
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
          <Form.Item
            label="Stripe payment description"
            name={['registration', 'stripeDescription']}
            rules={[REQUIRED, { max: 200, message: '200 characters or fewer' }]}
            extra="Shown in Stripe and on receipts, followed by the number of players, like “x 4”."
          >
            <Input />
          </Form.Item>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={5} className={styles.sectionTitle} style={{ margin: 0 }}>Sponsors</Title>
          </div>
          <Form.Item label="Sponsor interest form link" name={['sponsors', 'interestFormUrl']} rules={[REQUIRED]}>
            <Input />
          </Form.Item>

          <Form.List name={['sponsors', 'tiers']}>
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className={styles.tier}>
                    <div className={styles.tierHeader}>
                      {/* A live preview of the title the sponsor page will show. */}
                      <Form.Item noStyle shouldUpdate>
                        {() => (
                          <Text strong ellipsis className={styles.tierTitle}>
                            {sponsorTierTitle(form.getFieldValue(['sponsors', 'tiers', field.name]) || {})}
                          </Text>
                        )}
                      </Form.Item>
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                        aria-label="Move tier up"
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                        aria-label="Move tier down"
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        aria-label="Remove tier"
                      />
                    </div>

                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Tier name" name={[field.name, 'name']} rules={[REQUIRED]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item label="Price ($)" name={[field.name, 'price']} rules={[REQUIRED]}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item label="Spots" name={[field.name, 'spots']} extra="Blank for unlimited.">
                          <InputNumber min={1} precision={0} placeholder="Unlimited" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item label="Description" name={[field.name, 'description']} rules={[REQUIRED]}>
                      <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                    </Form.Item>

                    <Form.List name={[field.name, 'benefits']}>
                      {(benefitFields, { add: addBenefit, remove: removeBenefit }) => (
                        <div className={styles.subList}>
                          <Text className={styles.listLabel}>Benefits</Text>
                          {benefitFields.map((benefitField, benefitIndex) => (
                            <div key={benefitField.key} className={styles.listRow}>
                              <Form.Item name={[benefitField.name]}>
                                <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} aria-label={`Benefit ${benefitIndex + 1}`} />
                              </Form.Item>
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeBenefit(benefitField.name)}
                                aria-label={`Remove benefit ${benefitIndex + 1}`}
                              />
                            </div>
                          ))}
                          <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addBenefit('')}>
                            Add benefit
                          </Button>
                        </div>
                      )}
                    </Form.List>

                    <Form.List name={[field.name, 'sponsors']}>
                      {(sponsorFields, { add: addSponsor, remove: removeSponsor }) => (
                        <div className={styles.subList}>
                          <Text className={styles.listLabel}>
                            Sponsors {!sponsorFields.length && <Text type="secondary">· none yet</Text>}
                          </Text>
                          {sponsorFields.map((sponsorField, sponsorIndex) => (
                            <div key={sponsorField.key} className={styles.sponsorRow}>
                              <Form.Item name={[sponsorField.name, 'name']} rules={[REQUIRED]}>
                                <Input placeholder="Sponsor name" aria-label={`Sponsor ${sponsorIndex + 1} name`} />
                              </Form.Item>
                              <Form.Item name={[sponsorField.name, 'url']}>
                                <Input placeholder="Website (optional)" aria-label={`Sponsor ${sponsorIndex + 1} website`} />
                              </Form.Item>
                              <Form.Item name={[sponsorField.name, 'logoUrl']}>
                                <Input placeholder="Logo image link (optional)" aria-label={`Sponsor ${sponsorIndex + 1} logo`} />
                              </Form.Item>
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeSponsor(sponsorField.name)}
                                aria-label={`Remove sponsor ${sponsorIndex + 1}`}
                              />
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addSponsor({ name: '', url: '', logoUrl: '' })}
                          >
                            Add sponsor
                          </Button>
                        </div>
                      )}
                    </Form.List>

                    <Form.Item name={[field.name, 'showOnHomePage']} valuePropName="checked" style={{ marginBottom: 8 }}>
                      <Checkbox>Show this tier's sponsors on the home page</Checkbox>
                    </Form.Item>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add(newTier())}>
                    Add sponsor tier
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </section>
      </Form>

      {dirty && (
        <div className={styles.saveBar}>
          <Text style={{ flex: 1 }}>Unsaved changes</Text>
          <Button onClick={handleDiscard} disabled={saving}>Discard</Button>
          <Button type="primary" onClick={() => form.submit()} loading={saving}>Save</Button>
        </div>
      )}
    </div>
  );
}
